import { passaNoCanal } from './canalDaConversa';

/**
 * A cadeia de filtros da lista lateral do chat ao vivo.
 *
 * ## Por que isto saiu de dentro do RoomList
 *
 * Lá a lista não era derivada: `displayedRooms` era um ESTADO escrito de seis
 * lugares, e dois deles filtravam a lista sobre si mesma
 * (`handleSearch` fazia `displayedRooms.filter(...)` e gravava o resultado de
 * volta). Dois `useEffect` diferentes a reescreviam com dependências que se
 * sobrepõem, então a ordem em que os efeitos rodavam decidia o que o operador
 * via -- e um terceiro filtro entrando ali é onde aparecem as listas que não
 * voltam.
 *
 * Com a cadeia aqui, pura e num lugar só, os filtros passam a COMPOR: cada um
 * é um passo, a ordem é explícita, e nada reescreve o resultado do vizinho.
 *
 * ## O que foi corrigido no caminho, e o que NÃO foi
 *
 * Duas definições duplicadas existiam para a mesma pergunta, e em cada caso
 * ficou a que o operador de fato vê na tela:
 *
 * - **Não lidas.** `getRooms` filtrava por `room.unread`, campo que NÃO existe
 *   neste payload (`GET /rooms/{id}` devolve o model inteiro, cujo contador se
 *   chama `whatsapp_unread_count`; `unread` só existe no payload do Painel de
 *   Atendimentos). Aquele caminho dava sempre lista vazia. Ficou a definição
 *   por mensagem, que é a mesma do badge da linha.
 * - **Busca.** `clearSorting` procurava em nome, e-mail e código; `handleSearch`
 *   só em nome. Ficou a mais ampla, que é a que vencia na prática.
 *
 * Fora de escopo, de propósito, e seguem como estavam: `getRooms` não
 * atualizar a lista no modo "Todas as conversas", e o rótulo invertido da seta
 * de ordenação no `SearchBar`.
 */

/**
 * A conversa tem mensagem do cliente ainda não lida?
 *
 * `type === 0` é mensagem DO CLIENTE (`Chat/index.jsx:155` nomeia o autor pelo
 * cliente nesse caso), e `read_at` nela significa que o OPERADOR leu --
 * `Room::messageReadFocus()` é quem marca.
 *
 * A contagem é "existe alguma não lida", de propósito: é exatamente a conta do
 * badge da linha (`Room/index.jsx:97-101`). Assim a conversa que mostra badge
 * é a conversa que passa no filtro, por construção -- e não por coincidência
 * entre duas contas escritas separado.
 */
export const temMensagemNaoLida = (conversa) => {
    if (!Array.isArray(conversa?.messages)) return false;
    return conversa.messages.some((msg) => msg && msg.type === 0 && !msg.read_at);
};

/**
 * A conversa casa com o termo buscado?
 *
 * Nome, e-mail e código do cliente -- a definição mais ampla das duas que
 * existiam, e a que vencia. Sem termo, tudo passa.
 *
 * `client` pode ser nulo: visitante que chega por canal de porta híbrida entra
 * sem empresa, e a lista já derrubou inteira uma vez por ler dentro de `client`
 * sem proteger.
 */
export const passaNaBusca = (conversa, busca) => {
    const termo = String(busca || '').trim().toLowerCase();
    if (!termo) return true;

    const cliente = conversa?.client;
    if (!cliente) return false;

    return [cliente.name, cliente.email, cliente.code]
        .some((campo) => String(campo || '').toLowerCase().includes(termo));
};

/**
 * Ordena por `updated_at`, sem mexer no array recebido.
 *
 * Conversa sem `updated_at` vai para o começo em `asc` (epoch), que é o que a
 * ordenação antiga fazia -- `new Date(0)`.
 */
const ordenarPorAtualizacao = (conversas, ordem) => [...conversas].sort((a, b) => {
    const dataA = a?.updated_at ? new Date(a.updated_at).getTime() : 0;
    const dataB = b?.updated_at ? new Date(b.updated_at).getTime() : 0;
    return ordem === 'asc' ? dataA - dataB : dataB - dataA;
});

/**
 * A lista que a tela mostra, derivada da fonte.
 *
 * Os passos, nesta ordem: canal, não lidas, busca, ordenação. A ordem entre os
 * três filtros não muda o resultado (são conjunções), mas está fixa aqui para
 * a leitura ser sempre a mesma; a ordenação é necessariamente a última.
 *
 * `ordem` nula significa "o operador não clicou em ordenar" -- e aí a ordem é a
 * que a api devolveu, intocada. Antes de a cadeia ser unificada a ordenação era
 * aplicada de forma imperativa e o próximo poll a apagava, então na prática o
 * botão não funcionava; agora ela permanece. É a única mudança de
 * comportamento visível desta reorganização, e é o botão passando a fazer o
 * que o nome dele promete.
 */
export const filtrarConversas = ({
    conversas,
    canal,
    somenteNaoLidas = false,
    busca = '',
    ordem = null,
} = {}) => {
    if (!Array.isArray(conversas)) return [];

    const filtradas = conversas.filter((conversa) => (
        passaNoCanal(conversa, canal)
        && (!somenteNaoLidas || temMensagemNaoLida(conversa))
        && passaNaBusca(conversa, busca)
    ));

    return ordem ? ordenarPorAtualizacao(filtradas, ordem) : filtradas;
};
